import React, { useEffect, useState, useMemo, useRef } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, buildApiUrl } from '../config/api';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import RutaProgramadaPanel from './RutaProgramadaPanel';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DashboardSupervision = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [estadisticasPuntos, setEstadisticasPuntos] = useState([]);
  const [loadingEstadisticas, setLoadingEstadisticas] = useState(true);
  const [estadisticasVisitasPorHora, setEstadisticasVisitasPorHora] = useState([]);
  const [loadingVisitasPorHora, setLoadingVisitasPorHora] = useState(true);
  const [mostrarGraficoDia, setMostrarGraficoDia] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const rutaProgramadaPanelRef = useRef(null);

  // Redirigir vigilantes al dashboard de vigilancia
  useEffect(() => {
    if (user && user.rol !== 'S') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const fetchEstadisticas = async (showRefreshing = false) => {
      // Validar que el usuario sea supervisor
      if (!user || user?.rol !== 'S') {
        setError('Solo los supervisores pueden acceder a este dashboard.');
        setLoading(false);
        setLoadingEstadisticas(false);
        return;
      }

      // Los supervisores NO necesitan id_generado (puesto asignado)
      // Pueden ver información de todos los puntos

      if (!showRefreshing) {
        setLoading(true);
      }
      setLoadingEstadisticas(true);
      setLoadingVisitasPorHora(true);
      setError('');

      try {
        // Obtener estadísticas de supervisión del mes actual (turno DIA)
        const url = new URL(getApiUrl('SUPERVISION', 'ESTADISTICAS_SUPERVISION_MES'));
        url.searchParams.set('username', user?.username || '');

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        const result = await response.json();

        if (response.ok && result.success) {
          setEstadisticasPuntos(result.data || []);
        } else {
          console.error('Error obteniendo estadísticas de supervisión del mes:', result.error);
          setEstadisticasPuntos([]);
        }
      } catch (err) {
        console.error('Error cargando estadísticas de supervisión del mes:', err);
        setError(err.message || 'Error al cargar las estadísticas de supervisión.');
        setEstadisticasPuntos([]);
      } finally {
        setLoadingEstadisticas(false);
      }

      // Determinar qué gráfico mostrar según la hora actual
      const horaActual = new Date().getHours();
      const esTurnoDia = horaActual >= 7 && horaActual <= 18;
      setMostrarGraficoDia(esTurnoDia);

      // Obtener estadísticas de visitas por hora según el turno
      try {
        const endpoint = esTurnoDia 
          ? 'ESTADISTICAS_VISITAS_POR_HORA_DIA'
          : 'ESTADISTICAS_VISITAS_POR_HORA';
        
        const urlVisitas = new URL(getApiUrl('SUPERVISION', endpoint));
        urlVisitas.searchParams.set('username', user?.username || '');

        const responseVisitas = await fetch(urlVisitas.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        const resultVisitas = await responseVisitas.json();

        if (responseVisitas.ok && resultVisitas.success) {
          setEstadisticasVisitasPorHora(resultVisitas.data || []);
        } else {
          console.error('Error obteniendo estadísticas de visitas por hora:', resultVisitas.error);
          setEstadisticasVisitasPorHora([]);
        }
      } catch (err) {
        console.error('Error cargando estadísticas de visitas por hora:', err);
        setEstadisticasVisitasPorHora([]);
      } finally {
        setLoadingVisitasPorHora(false);
        if (!showRefreshing) {
          setLoading(false);
        }
        if (showRefreshing) {
          setRefreshing(false);
        }
      }
    };

  useEffect(() => {
    fetchEstadisticas();
  }, [user]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchEstadisticas(true);
  };

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6 space-y-6 pb-32 mb-8">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Supervisión</h1>
            <p className="text-sm text-gray-600 mt-1">Vista general de la supervisión y control de relevos</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className={`flex items-center justify-center p-2 rounded-lg border transition-colors flex-shrink-0 ${
              refreshing || loading
                ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
            }`}
            title="Actualizar datos"
          >
            <svg 
              className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
              />
            </svg>
          </button>
        </div>

        <RutaProgramadaPanel ref={rutaProgramadaPanelRef} variant="embedded" />

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow">
            <div className="inline-flex items-center space-x-2">
              <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm text-gray-600">Cargando estadísticas…</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
            <p className="text-sm font-medium">{error}</p>
          </div>
        ) : (
          <>
            {/* Gráfico de visitas por hora (Turno DIA o NOCHE según hora actual) */}
            {estadisticasVisitasPorHora.length > 0 && (
              <section>
                <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                  <div className="mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Visitas por Hora - Turno {mostrarGraficoDia ? 'DIA' : 'NOCHE'}
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                      {mostrarGraficoDia 
                        ? 'Cantidad de puntos visitados por cada hora del turno diurno (07:00 - 18:59)'
                        : 'Cantidad de puntos visitados por cada hora del turno nocturno (19:00 - 06:59)'
                      }
                    </p>
                  </div>
                  {loadingVisitasPorHora ? (
                    <div className="h-64 flex items-center justify-center">
                      <div className="inline-flex items-center space-x-2">
                        <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-sm text-gray-600">Cargando gráfico…</span>
                      </div>
                    </div>
                  ) : (
                    <div className="h-64">
                      <Line
                        data={{
                          labels: estadisticasVisitasPorHora.map(item => item.horaFormateada),
                          datasets: [
                            {
                              label: `Puntos Visitados (${estadisticasVisitasPorHora.reduce((sum, item) => sum + (item.puntosVisitados || 0), 0)})`,
                              data: estadisticasVisitasPorHora.map(item => item.puntosVisitados),
                              backgroundColor: mostrarGraficoDia 
                                ? 'rgba(59, 130, 246, 0.1)' 
                                : 'rgba(139, 92, 246, 0.1)',
                              borderColor: mostrarGraficoDia 
                                ? 'rgba(59, 130, 246, 1)' 
                                : 'rgba(139, 92, 246, 1)',
                              borderWidth: 2,
                              pointBackgroundColor: mostrarGraficoDia 
                                ? 'rgba(59, 130, 246, 1)' 
                                : 'rgba(139, 92, 246, 1)',
                              pointBorderColor: '#fff',
                              pointBorderWidth: 2,
                              pointRadius: 4,
                              pointHoverRadius: 6,
                              fill: true,
                              tension: 0.4
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top',
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                stepSize: 1
                              }
                            },
                            x: {
                              ticks: {
                                maxRotation: 45,
                                minRotation: 45
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Gráfico de barras - Registros por punto (mes actual, turno DIA) */}
            {estadisticasPuntos.length > 0 && (
              <section>
                <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                  <div className="mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Registros por Punto - Mes Actual</h2>
                    <p className="text-xs text-gray-500 mt-1">
                      Supervisiones realizadas en turno DIA (07:00-19:00), NOCHE (19:00-06:59) y Total
                    </p>
                  </div>
                  {loadingEstadisticas ? (
                    <div className="h-64 flex items-center justify-center">
                      <div className="inline-flex items-center space-x-2">
                        <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-sm text-gray-600">Cargando gráfico…</span>
                      </div>
                    </div>
                  ) : (
                    <div className="h-64">
                      <Line
                        data={{
                          labels: estadisticasPuntos.map(item => item.punto),
                          datasets: [
                            {
                              label: 'Turno DIA',
                              data: estadisticasPuntos.map(item => item.dia || 0),
                              backgroundColor: 'rgba(59, 130, 246, 0.3)',
                              borderColor: 'rgba(59, 130, 246, 1)',
                              borderWidth: 2,
                              pointBackgroundColor: 'rgba(59, 130, 246, 1)',
                              pointBorderColor: '#fff',
                              pointBorderWidth: 2,
                              pointRadius: 4,
                              pointHoverRadius: 6,
                              fill: true,
                              tension: 0.4
                            },
                            {
                              label: 'Turno NOCHE',
                              data: estadisticasPuntos.map(item => item.noche || 0),
                              backgroundColor: 'rgba(139, 92, 246, 0.3)',
                              borderColor: 'rgba(139, 92, 246, 1)',
                              borderWidth: 2,
                              pointBackgroundColor: 'rgba(139, 92, 246, 1)',
                              pointBorderColor: '#fff',
                              pointBorderWidth: 2,
                              pointRadius: 4,
                              pointHoverRadius: 6,
                              fill: true,
                              tension: 0.4
                            },
                            {
                              label: 'Total',
                              data: estadisticasPuntos.map(item => item.total || 0),
                              backgroundColor: 'rgba(34, 197, 94, 0.3)',
                              borderColor: 'rgba(34, 197, 94, 1)',
                              borderWidth: 3,
                              pointBackgroundColor: 'rgba(34, 197, 94, 1)',
                              pointBorderColor: '#fff',
                              pointBorderWidth: 2,
                              pointRadius: 5,
                              pointHoverRadius: 7,
                              fill: true,
                              tension: 0.4
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top',
                              align: 'center',
                              labels: {
                                boxWidth: 15,
                                boxHeight: 15,
                                padding: 15,
                                usePointStyle: false,
                                font: {
                                  size: 12
                                }
                              }
                            },
                            tooltip: {
                              callbacks: {
                                afterLabel: (context) => {
                                  const dataIndex = context.dataIndex;
                                  const item = estadisticasPuntos[dataIndex];
                                  return item.nombre || '';
                                }
                              }
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                stepSize: 1
                              }
                            },
                            x: {
                              ticks: {
                                maxRotation: 45,
                                minRotation: 45
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Accesos Rápidos */}
            <section>
              <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Accesos Rápidos</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <button
                    onClick={() => navigate('/supervision/registrar-asistencia')}
                    className="flex items-center p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200 hover:from-indigo-100 hover:to-purple-100 transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Registrar Asistencia</p>
                      <p className="text-xs text-gray-600 mt-1">Registrar asistencia de vigilantes</p>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate('/supervision/control-relevos')}
                    className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200 hover:from-blue-100 hover:to-cyan-100 transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Control de Relevos</p>
                      <p className="text-xs text-gray-600 mt-1">Gestionar y supervisar relevos</p>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate('/supervision/visitas-realizadas')}
                    className="flex items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:from-green-100 hover:to-emerald-100 transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Visitas Realizadas</p>
                      <p className="text-xs text-gray-600 mt-1">Consultar visitas registradas</p>
                    </div>
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
};

export default DashboardSupervision;

